import React, { PropTypes } from 'react';
import { v4 } from 'node-uuid';
import idx from 'idx';
import isEqual from 'lodash/isEqual';
import { LoadingIndicator } from 'common/components/preloader';
import {
  trackMixpanel,
  MP_VIEWED_MARKETING_PAGE_EVENT_NAME, MP_VIEWED_LANDING_PAGE_EVENT_NAME,
  MP_CLICKED_GET_STARTED_EVENT_NAME, MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME,
  MP_CLICKED_READ_REVIEWS_EVENT_NAME, MP_CLICKED_TESTIMONIALS_ARROW_EVENT_NAME,
  MP_CLICKED_1_TO_1_FAQ_ITEM_EVENT_NAME
} from 'common/tracking';

import Meta from 'landing_pages/components/meta';

import Author from 'landing_pages/components/author';
import Benefits from 'landing_pages/components/benefits';
import Cta from 'marketing/common/cta';
import Comparison from 'landing_pages/components/comparison';
import EmailSubscriptionForm from 'landing_pages/components/email_subscription_form';
import ExternalPage from 'landing_pages/components/external_page';
import Faq from 'landing_pages/components/faq';
import Featured from 'marketing/common/featured';
import Gallery from 'landing_pages/components/gallery';
import Hero from 'landing_pages/components/hero';
import How from 'landing_pages/components/how';
import How2 from 'landing_pages/components/how_2';
import How3 from 'landing_pages/components/how_3';
import Links from 'landing_pages/components/links';
import Pricing from 'landing_pages/components/pricing';
import PromoBanner from 'landing_pages/components/promo_banner';
import Share from 'landing_pages/components/share';
import SingleTestimonial from 'landing_pages/components/single_testimonial';
import SubscriptionHero from 'landing_pages/components/subscription_hero';
import SubscriptionHero2 from 'landing_pages/components/subscription_hero_2';
import Stats from 'landing_pages/components/stats';
import Support from 'marketing/containers/support';
import Testimonials from 'landing_pages/components/testimonials';
import Story from 'landing_pages/components/story';
import Reviews from 'landing_pages/containers/reviews';
import BlogGallery from 'landing_pages/components/blog_gallery';
import SubCategories from 'landing_pages/containers/sub_categories';
import Differentiators from 'landing_pages/components/differentiators';
import SelectPackageSubcat from 'landing_pages/containers/select_package_subcat';
import Testimonials2 from 'landing_pages/components/testimonials_2';
import ErrorPage from 'error/components';
import IntercomChat from 'common/containers/intercom_chat';
import { activateExperiment } from 'common/optimize';

const BLOCK_MAP = {
  author: Author,
  benefits: Benefits,
  blog_gallery: BlogGallery,
  comparison: Comparison,
  cta: Cta,
  differentiators: Differentiators,
  email_subscription_form: EmailSubscriptionForm,
  external_page: ExternalPage,
  faq: Faq,
  featured: Featured,
  gallery: Gallery,
  hero: Hero,
  how_it_works: How,
  how_it_works_2: How2,
  how_it_works_3: How3,
  links: Links,
  pricing_package: Pricing,
  promo_banner: PromoBanner,
  reviews: Reviews,
  select_package_sub_category: SelectPackageSubcat,
  share: Share,
  single_testimonial: SingleTestimonial,
  stats: Stats,
  story: Story,
  sub_categories: SubCategories,
  subscription_hero: SubscriptionHero,
  subscription_hero_2: SubscriptionHero2,
  subscription_hero_ebook: SubscriptionHero,
  support: Support,
  testimonial: Testimonials,
  testimonials: Testimonials,
  testimonials_2: Testimonials2,
};

class LandingPage extends React.Component {
  constructor(props) {
    super(props);

    this.activateExperiments = this.activateExperiments.bind(this);
  }

  activateExperiments() {
    this.props.currentExperiments.forEach(exp => activateExperiment(exp.id, exp.variationId));
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.currentExperiments, this.props.currentExperiments)) {
      this.activateExperiments();
    }
  }

  componentDidMount() {
    this.props.fetch();
    trackMixpanel(
      MP_VIEWED_LANDING_PAGE_EVENT_NAME,
      {
        "pageSlug": this.props.params.pageSlug
      }
    )
    trackMixpanel(
      MP_VIEWED_MARKETING_PAGE_EVENT_NAME,
      {
        "pageName": "Landing page"
      }
    )

    this.activateExperiments();
  }

  renderBlock(block, i) {
    const { landing_page } = this.props;

    if (!block) {
      return null;
    }

    const Component = BLOCK_MAP[block.block_type];
    if (!Component) {
      return null;
    }

    const blockData = block.data;

    if (
      block.block_type == "cta" &&
      (idx(blockData, _ => _.cta.label) || '').toLowerCase() === "get started"
    ) {
      blockData["eventTracking"] = {
        eventName: MP_CLICKED_GET_STARTED_EVENT_NAME,
        eventData: {
          pageName: "Landing page",
          landingSlug: this.props.params.pageSlug,
          pageArea: "CTA",
        }
      };
    }

    if (
      block.block_type == "cta" &&
      (idx(blockData, _ => _.cta.url) || '').indexOf('consultation-chinese') > 1
    ) {
      blockData["eventTracking"] = {
        eventName: MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME,
        eventData: {
          pageName: "Landing page",
          landingSlug: this.props.params.pageSlug,
          version: "chinese",
          pageArea: "CTA",
        }
      };
    } else if (
      block.block_type == "cta" &&
      (idx(blockData, _ => _.cta.url) || '').indexOf('consultation') > 1
    ) {
      blockData["eventTracking"] = {
        eventName: MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME,
        eventData: {
          pageName: "Landing page",
          landingSlug: this.props.params.pageSlug,
          version: "english",
          pageArea: "CTA",
        }
      };
    }

    if (
      block.block_type == "hero" &&
      (idx(blockData, _ => _.cta.label) || '').toLowerCase() === "get started"
    ) {
      blockData["getStartedEventTracking"] = {
        eventName: MP_CLICKED_GET_STARTED_EVENT_NAME,
        eventData: {
          pageName: "Landing page",
          landingSlug: this.props.params.pageSlug,
          pageArea: "Hero",
        }
      };
    }

    if (
      block.block_type == "hero" &&
      (idx(blockData, _ => _.cta.label) || '').toLowerCase() === "discover designers"
    ) {
      blockData["getStartedEventTracking"] = {
        eventName: "Clicked Discover Designers",
        eventData: {
          pageName: "Landing page",
          landingSlug: this.props.params.pageSlug,
          pageArea: "Hero",
        }
      };
    }

    if (block.block_type == "faq" && this.props.params.pageSlug == "1to1-projects") {
      blockData["eventTracking"] = {
        eventName: MP_CLICKED_1_TO_1_FAQ_ITEM_EVENT_NAME,
        eventData: {
          pageName: "Landing page",
          landingSlug: this.props.params.pageSlug,
          pageArea: "1-to-1 FAQs",
        }
      }
    }

    if (block.block_type == "support") {
      blockData["consultationEventTracking"] = {
        eventName: MP_CLICKED_DESIGN_CONSULTATION_EVENT_NAME,
        eventData: {
          pageName: "Landing page",
          version: "english",
          landingSlug: this.props.params.pageSlug,
          pageArea: "Support footer",
        }
      }
    }

    if (block.block_type == "testimonials") {
      blockData["linkEventTracking"] = {
        eventName: MP_CLICKED_READ_REVIEWS_EVENT_NAME,
        eventData: {
          pageName: "Landing page",
          landingSlug: this.props.params.pageSlug,
          pageArea: "Testimonial slider",
        }
      };
      blockData["arrowEventTracking"] = {
        eventName: MP_CLICKED_TESTIMONIALS_ARROW_EVENT_NAME,
        eventData: {
          pageName: "Landing page",
          landingSlug: this.props.params.pageSlug,
          pageArea: "Testimonial slider",
        }
      }
    }

    return (
      <Component key={i}
        {...blockData}
        blockType={block.block_type}
        pageSeoTitle={landing_page.seo_title}
        pageUrlStem={landing_page.url_stem}
      />
    );
  }

  render() {
    const { landing_page, error } = this.props;
    if (!landing_page && !error) {
      return (<div className="l-content marketing" id="content">
        <LoadingIndicator />
      </div>);
    }

    if (error) {
      const mockRouter = { location: { pathname: '404' } };
      return (<ErrorPage router={mockRouter} />);
    }

    return (<div className="l-content marketing" id="content">
      <Meta {...landing_page} />
      {landing_page.page_blocks && landing_page.page_blocks.map(this.renderBlock.bind(this))}
      <IntercomChat />
    </div>);
  }
}


LandingPage.propTypes = {
  fetch: PropTypes.func.isRequired,
  landing_page: PropTypes.object,
  error: PropTypes.object,
};

export default LandingPage;



// WEBPACK FOOTER //
// ./landing_pages/components/index.js